import { Flex, Spacer } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { JSX } from 'react/jsx-runtime'
import iconAudioSpeaker from '~/assets/icons-light-theme/audio-player/icon-audio-speaker-dark-900.svg'
import iconCallHistory_dark900 from '~/assets/icons-light-theme/icon-callHistory-dark900.svg'
import iconExperiment_dark900 from '~/assets/icons-light-theme/icon-experiment-dark900.svg'
import iconFaxing_dark900 from '~/assets/icons-light-theme/icon-faxing-dark900.svg'
import iconInfo_dark800 from '~/assets/icons-light-theme/icon-info-dark-800.svg'
import iconNotificationsBell_dark900 from '~/assets/icons-light-theme/icon-notificationsBell-dark900.svg'
import iconPersonal_dark900 from '~/assets/icons-light-theme/icon-personal-dark900.svg'
import iconTexting_dark900 from '~/assets/icons-light-theme/icon-texting-dark900.svg'
import iconBlocked from '~/assets/svgs/icon-blocked.svg'
import iconHeadset from '~/assets/svgs/icon-headset.svg'
import { useUserState } from '~/atoms/UserState'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { useAppVersion } from '~/hooks/useAppVersion'
import { ApplicationRoutes } from '~/hooks/useRouting'
import UpdaterButton from '~/pages/Settings/UpdaterButton'
import { SettingsType } from '~/types/Settings'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

import { AudioSettings } from './AudioSettings'
import { BlockedNumbers } from './BlockedNumbers'
import { CallCenterSettings } from './CallCenterSettings'
import { ExperimentSettings } from './ExperimentSettings'
import { FaxSettings } from './FaxSettings'
import { NotificationSettings } from './NotificationSettings'
import { PhoneSettings } from './PhoneSettings'
import { ProfileSettings } from './ProfileSettings'
import {
  PageContainer,
  SettingControlsWrapper,
  SettingsHeader,
  SettingsTab,
  TabList,
  Version,
} from './SettingsPage.style'
import KeyboardShortcutsDetail from './ShortcutKeysHelp'
import { SnippetsSettings } from './SnippetsSettings'

type SettingOptionType = {
  label: string
  value: SettingsType
  icon: string
  iconHeight?: number
  badge?: string
}

export const SettingsPage = () => {
  const appVersion = useAppVersion()
  const { t } = useTranslation()

  const {
    pbxId,
    currentUserExtensionId: extensionId,
    agentStatusesEnabled,
  } = useUserState()

  const navigate = useNavigate()
  const { setting } = useParams()
  const selectedMenu = setting ?? SettingsType.PROFILE

  const baseSettingsOptions: SettingOptionType[] = [
    {
      label: t('baseSettings.profile'),
      value: SettingsType.PROFILE,
      icon: iconPersonal_dark900,
      iconHeight: 20,
      badge: isElectron ? '' : t('baseSettings.new'),
    },
    {
      label: t('baseSettings.snippets'),
      value: SettingsType.SNIPPETS,
      icon: iconTexting_dark900,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.phone'),
      value: SettingsType.PHONE,
      icon: iconCallHistory_dark900,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.fax'),
      value: SettingsType.FAX,
      icon: iconFaxing_dark900,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.notifications'),
      value: SettingsType.NOTIFICATIONS,
      icon: iconNotificationsBell_dark900,
    },
    {
      label: t('baseSettings.audio'),
      value: SettingsType.AUDIO,
      icon: iconAudioSpeaker,
    },
    {
      label: t('baseSettings.blockedNumbers'),
      value: SettingsType.BLOCKED_NUMBERS,
      icon: iconBlocked,
      iconHeight: 20,
    },
    {
      label: t('baseSettings.experiments'),
      value: SettingsType.EXPERIMENTS,
      icon: iconExperiment_dark900,
      badge: isElectron ? t('baseSettings.new') : '',
    },
    {
      label: t('baseSettings.shortcutKeys'),
      value: SettingsType.SHORTCUTS,
      icon: iconInfo_dark800,
    },
  ]

  const callCenterOption = {
    label: t('baseSettings.callCenter'),
    value: SettingsType.CALL_CENTER,
    icon: iconHeadset,
    iconHeight: 20,
  }

  const settingsOptions = agentStatusesEnabled
    ? [
        ...baseSettingsOptions.slice(0, 7),
        callCenterOption,
        ...baseSettingsOptions.slice(7),
      ]
    : baseSettingsOptions

  const handleChangeMenu = (menuItem: {
    label: any
    value: any
    icon?: string
    iconHeight?: number | undefined
    badge?: string | undefined
  }) => {
    window.gtag('event', 'settings_menu_changed', {
      label: menuItem.label,
      value: menuItem.value,
    })
    navigate(`${ApplicationRoutes.SETTINGS}/${menuItem.value}`)
  }

  const RenderMenuList = () => {
    let MenuList: JSX.Element = <></>

    switch (selectedMenu) {
      case SettingsType.PROFILE:
        MenuList = <ProfileSettings />
        break

      case SettingsType.SNIPPETS:
        MenuList = <SnippetsSettings />
        break

      case SettingsType.PHONE:
        MenuList = <PhoneSettings pbxId={pbxId} />
        break

      case SettingsType.EXPERIMENTS:
        MenuList = <ExperimentSettings />
        break

      case SettingsType.NOTIFICATIONS:
        MenuList = <NotificationSettings />
        break

      case SettingsType.AUDIO:
        MenuList = <AudioSettings />
        break

      case SettingsType.CALL_CENTER:
        MenuList = (
          <CallCenterSettings pbxId={pbxId} extensionId={extensionId} />
        )
        break

      case SettingsType.BLOCKED_NUMBERS:
        MenuList = <BlockedNumbers />
        break

      case SettingsType.SHORTCUTS:
        MenuList = <KeyboardShortcutsDetail />
        break

      case SettingsType.FAX:
        MenuList = <FaxSettings pbxId={pbxId} />
        break

      default:
        break
    }

    return MenuList
  }

  return (
    <PageContainer>
      <TabList width={'100%'}>
        <SettingsHeader>{t('settings')}</SettingsHeader>
        {settingsOptions.map((menu: SettingOptionType, index) => {
          const selected = selectedMenu === menu.value
          return (
            <SettingsTab
              key={`menu-${menu.label}-${index}`}
              icon={menu.icon}
              active={selected}
              onClick={() => handleChangeMenu(menu)}
              label={menu.label}
            >
              <Flex alignItems={'center'}>
                {(menu.badge ?? false) && (
                  <NotificationBadge
                    count={menu.badge ?? ''}
                    size={Size.md}
                    variant={NotificationBadgeVariant.New}
                    sx={{
                      paddingLeft: '.5rem',
                    }}
                  />
                )}
              </Flex>
            </SettingsTab>
          )
        })}
        <Spacer />
        <UpdaterButton />
        <Version>{`v${appVersion}`}</Version>
      </TabList>
      <SettingControlsWrapper>{RenderMenuList()}</SettingControlsWrapper>
    </PageContainer>
  )
}
